import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, Input, InputNumber, Select, Button, Empty, Tag, Space, Modal, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage, showWarningMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import { ScreenHeader, QuantityStep, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import MachineSelector from '../../components/machine/MachineSelector';
import { warnIfMachineNearCapacity } from '../../components/machine/machineOccupancy';
import JustificationModal, { JustificationInput, isJustificationValid } from '../../components/modals/JustificationModal';

/**
 * Fluxo dedicado "Adicionar RC à máquina" (PWA do operador).
 *
 * Separado do RcPanel (saldo diário de RC) para deixar a Home do operador com
 * um card próprio e o fluxo mais direto: escolher a máquina -> informar litros
 * -> (observação) -> registrar. Lista em cards as adições de hoje.
 *
 * Correções (regra do cliente): Admin, Operador-comprador e Líder editam ou
 * excluem as adições DE HOJE de qualquer operador — para não-admin a
 * justificativa é obrigatória e vai para a auditoria.
 *
 * A máquina fica em memória durante o uso desta tela (escolhida ao abrir;
 * re-selecionada ao reabrir o fluxo pela Home).
 *
 * Recarga de solvente amarelo: mesma tela, parametrizada por
 * `location.state.source = 'YELLOW_SOLVENT'` (card próprio na Home e opção na
 * modal de processamento). Entra na máquina como RC, mas desconta do estoque
 * de amarelo (com aviso se exceder o disponível) e não entra no RC diário.
 */
const RcConsumptionPanel = () => {
  const { loggedUser } = useContext(GeneralContext);
  const location = useLocation();
  const isAdmin = loggedUser?.type === EUserTypes.PANEL_ADMIN.code;
  const source = location.state?.source === 'YELLOW_SOLVENT' ? 'YELLOW_SOLVENT' : 'RC';
  const isYellow = source === 'YELLOW_SOLVENT';
  const flowColor = location.state?.color || (isYellow ? FLOW_COLORS.yellowConsumption : FLOW_COLORS.rcConsumption);
  // Comprador/líder (e admin) podem corrigir registros de hoje pelo PWA.
  const canCorrect = EUserTypes.canEditToday(loggedUser);
  // Justificativa obrigatória para não-admin (validada também na API).
  const justRequired = !isAdmin;

  const [form] = Form.useForm();
  const [machine, setMachine] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Correção no PWA: modal de edição (campos + justificativa) e alvo de exclusão.
  const [machines, setMachines] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.operation.listRcConsumptionsToday();
      setList(res?.data?.rows || []);
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar adições de RC.');
    } finally {
      setLoading(false);
    }
  };

  // Lista de máquinas para o select do modal de correção.
  const loadMachines = async () => {
    try {
      const res = await api.machine.list({ page: 1, pageSize: 100 });
      setMachines(res.data?.rows || []);
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { load(); if (canCorrect) loadMachines(); }, []);

  // Ao escolher a máquina, avisa se ela está com ocupação elevada (>= 90% da
  // capacidade) antes de o operador adicionar mais RC. Não bloqueia o fluxo.
  const handleMachineChange = (m) => {
    setMachine(m);
    if (m?.id) warnIfMachineNearCapacity(m.id);
  };

  const handleAdd = async (values) => {
    if (!machine?.id) {
      showErrorMessage('Selecione a máquina onde a recarga foi adicionada.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.operation.createRcConsumption({
        machine_id: machine.id,
        liters: values.consumption_liters,
        observation: values.consumption_observation || null,
        source,
      });
      showSuccessMessage(isYellow ? 'Recarga de amarelo registrada!' : 'Adição de RC registrada!');
      // A API registra mesmo quando excede o estoque de amarelo, mas devolve
      // `warning` para o operador saber e avisar o líder.
      if (res?.data?.warning) showWarningMessage(res.data.warning);
      form.resetFields();
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao registrar adição de RC.');
    } finally {
      setSaving(false);
    }
  };

  /** Salva a edição feita no modal do PWA (campos + justificativa). */
  const handleMobileUpdate = async () => {
    if (!editModal) return;
    setSavingEdit(true);
    try {
      await api.operation.updateRcConsumption(editModal.id, { ...editRecord, justification: editJustification.trim() || undefined });
      showSuccessMessage('Adição de RC corrigida!');
      setEditModal(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao corrigir adição de RC.');
    } finally {
      setSavingEdit(false);
    }
  };

  /** Exclui via modal de justificativa (PWA). */
  const handleMobileDelete = async (justification) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.operation.deleteRcConsumption(deleteTarget.id, { justification: justification || undefined });
      showSuccessMessage('Adição de RC removida!');
      setDeleteTarget(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover adição de RC.');
    } finally {
      setDeleting(false);
    }
  };

  /** Abre o modal de correção com os valores atuais do registro. */
  const openEditModal = (r) => {
    setEditRecord({ machine_id: r.machine_id, liters: Number(r.liters), observation: r.observation, source: r.source || 'RC' });
    setEditJustification('');
    setEditModal(r);
  };

  // Modal de correção do PWA (comprador/líder/admin): campos + justificativa.
  const mobileEditModal = (
    <Modal
      title="Corrigir adição de RC"
      open={!!editModal}
      onCancel={() => setEditModal(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setEditModal(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          loading={savingEdit}
          disabled={!isJustificationValid(editJustification, justRequired)}
          onClick={handleMobileUpdate}
        >
          Salvar correção
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Tipo</div>
          <Select
            value={editRecord?.source || 'RC'}
            onChange={(v) => setEditRecord((p) => ({ ...p, source: v }))}
            options={[
              { value: 'RC', label: 'RC (recarga comum)' },
              { value: 'YELLOW_SOLVENT', label: 'Solvente Amarelo' },
            ]}
            style={{ width: '100%' }}
            size="large"
            aria-label="Tipo da recarga"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Máquina</div>
          <Select
            value={editRecord?.machine_id}
            onChange={(v) => setEditRecord((p) => ({ ...p, machine_id: v }))}
            options={machines.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
            style={{ width: '100%' }}
            size="large"
            aria-label="Máquina da adição de RC"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Litros</div>
          <InputNumber
            value={editRecord?.liters}
            onChange={(v) => setEditRecord((p) => ({ ...p, liters: v }))}
            min={0}
            step={0.1}
            decimalSeparator=","
            style={{ width: '100%' }}
            size="large"
            aria-label="Litros da adição de RC"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Observação</div>
          <Input.TextArea
            value={editRecord?.observation || ''}
            onChange={(e) => setEditRecord((p) => ({ ...p, observation: e.target.value }))}
            autoSize={{ minRows: 2 }}
            aria-label="Observação da adição de RC"
          />
        </div>
        <JustificationInput value={editJustification} onChange={setEditJustification} required={justRequired} />
      </div>
    </Modal>
  );

  const mobileDeleteModal = (
    <JustificationModal
      open={!!deleteTarget}
      title="Excluir adição de RC"
      description={deleteTarget ? (
        <span>
          Excluir a adição de <strong>{deleteTarget.liters} L</strong>
          {deleteTarget.source === 'YELLOW_SOLVENT' ? <> de <strong>solvente amarelo</strong></> : null}
          {deleteTarget.machine ? <> na máquina <strong>{deleteTarget.machine.code}</strong></> : null}?
        </span>
      ) : null}
      confirmLabel="Excluir"
      danger
      loading={deleting}
      required={justRequired}
      onConfirm={handleMobileDelete}
      onCancel={() => setDeleteTarget(null)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BackToHome color={flowColor} />

      <Card loading={loading}>
        <ScreenHeader
          title={isYellow ? 'Recarga de Amarelo à máquina' : 'Adicionar RC à máquina'}
          subtitle={isYellow
            ? 'Registre o solvente amarelo que voltou do estoque para a máquina.'
            : 'Registre cada adição de recarga a uma máquina.'}
          color={flowColor}
        />
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <div style={{ marginBottom: 16 }}>
            <MachineSelector value={machine} onChange={handleMachineChange} required color={flowColor} />
          </div>
          <QuantityStep
            form={form}
            name="consumption_liters"
            label="Litros adicionados"
            hint={isYellow
              ? 'Quantos litros de solvente amarelo voltaram para a máquina.'
              : 'Quantos litros de RC foram adicionados à máquina.'}
          />
          <Form.Item label="Observação" name="consumption_observation">
            <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Opcional" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              size="large"
              block
              style={primaryButtonStyle(flowColor)}
            >
              Registrar adição
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card loading={loading}>
        <ScreenHeader
          title="Adições de hoje"
          subtitle={isYellow
            ? 'Recargas (RC e amarelo) adicionadas às máquinas no dia atual.'
            : 'RC adicionado às máquinas no dia atual.'}
          color={flowColor}
        />
        {list.length === 0 ? (
          <Empty description="Nenhuma adição registrada hoje." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {list.map((r) => (
              <div
                key={r.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  background: '#fafbff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space size={4}>
                    {r.machine ? <Tag color="orange">{r.machine.code}</Tag> : <Tag>—</Tag>}
                    {r.source === 'YELLOW_SOLVENT'
                      ? <Tag color="gold">Amarelo</Tag>
                      : <Tag color="blue">RC</Tag>}
                  </Space>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ fontSize: 18 }}>{r.liters} L</strong>
                    {canCorrect && (
                      <Space size={4}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(r)} aria-label="Corrigir adição de RC" />
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setDeleteTarget(r)} aria-label="Excluir adição de RC" />
                      </Space>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {new Date(r.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  {r.user ? ` · ${r.user.full_name}` : ''}
                  {r.observation ? ` · ${r.observation}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {mobileEditModal}
      {mobileDeleteModal}

      {/* Admin: atalho para o histórico filtrável no RcPanel desktop */}
      {isAdmin && (
        <Card size="small">
          <Button type="link" onClick={() => (window.location.href = '/operations/rc')}>
            Ver histórico filtrável de adições (painel admin)
          </Button>
        </Card>
      )}
    </div>
  );
};

export default RcConsumptionPanel;
